import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiTarget, FiTrendingUp, FiSettings, FiShield, FiUsers, FiArrowUpRight, FiArrowRight } = FiIcons;

const Services = () => {
  const services = [
    {
      icon: FiTarget,
      title: 'Strategic Planning & Market Entry',
      description: 'Comprehensive roadmaps for sustainable growth within South Africa\'s dynamic economic environment. Specialising in market entry strategies for international companies and expansion planning for local enterprises.',
      features: [
        'Competitive landscape analysis',
        'Regulatory framework assessment',
        'Stakeholder mapping',
        'BEE compliance strategy',
        'Implementation timelines'
      ],
      link: '/services/strategic-planning'
    },
    {
      icon: FiSettings,
      title: 'Operational Excellence & Process Optimisation',
      description: 'Streamline operations and maximise efficiency while maintaining quality standards and local employment commitments.',
      features: [
        'Process mapping and analysis',
        'Efficiency improvement programs',
        'Quality management systems',
        'Cost reduction strategies',
        'Performance metrics development'
      ],
      link: '/services'
    },
    {
      icon: FiTrendingUp,
      title: 'Digital Transformation & Technology Integration',
      description: 'Navigate comprehensive technology transformation programmes addressing South Africa\'s unique infrastructure challenges while maximising mobile-first opportunities.',
      features: [
        'ERP implementations',
        'Data analytics platforms',
        'CRM system integration',
        'AI applications',
        'Mobile-first solutions'
      ],
      link: '/services/digital-transformation'
    },
    {
      icon: FiShield,
      title: 'Regulatory Compliance & Risk Management',
      description: 'Expert guidance through complex regulatory frameworks with comprehensive risk mitigation strategies tailored to South African business environment.',
      features: [
        'Compliance audits',
        'Risk assessment frameworks',
        'Regulatory change management',
        'Policy development',
        'Training and implementation'
      ],
      link: '/services'
    },
    {
      icon: FiArrowUpRight,
      title: 'Merger & Acquisition Advisory',
      description: 'Strategic guidance for M&A transactions with deep understanding of local market dynamics, regulatory requirements, and cultural considerations.',
      features: [
        'Due diligence support',
        'Valuation analysis',
        'Integration planning',
        'Regulatory navigation',
        'Post-merger optimization'
      ],
      link: '/services'
    },
    {
      icon: FiUsers,
      title: 'Leadership Development & Change Management',
      description: 'Build leadership capabilities and manage organizational transformation effectively within multi-cultural South African business environments.',
      features: [
        'Leadership assessment',
        'Executive coaching',
        'Change strategy development',
        'Team building programs',
        'Cultural integration'
      ],
      link: '/services'
    }
  ];

  const industries = [
    {
      name: 'Mining & Resources',
      description: 'Deep expertise in mining operations, regulatory compliance, and sustainable resource management.',
      image: 'https://images.unsplash.com/photo-1578662996442-48f60103fc96?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80'
    },
    {
      name: 'Financial Services',
      description: 'Strategic guidance for banks, insurance companies, and fintech innovations.',
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80'
    },
    {
      name: 'Manufacturing',
      description: 'Operational excellence and digital transformation for manufacturing enterprises.',
      image: 'https://images.unsplash.com/photo-1565793298595-6a879b1d9492?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80'
    },
    {
      name: 'Technology',
      description: 'Growth strategies and market positioning for emerging technology companies.',
      image: 'https://images.unsplash.com/photo-1518709268805-4e9042af2176?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80'
    }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-purple-900 to-purple-700 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-4xl lg:text-5xl font-bold mb-6 font-serif">
              Comprehensive Consulting Services
            </h1>
            <p className="text-xl text-gray-200 leading-relaxed">
              Strategic solutions across all aspects of business transformation, from initial planning to successful implementation, tailored for the South African market.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white border border-gray-200 rounded-xl p-8 hover:shadow-xl transition-shadow group"
              >
                <div className="flex items-start space-x-4 mb-6">
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center group-hover:bg-purple-200 transition-colors">
                    <SafeIcon icon={service.icon} className="w-6 h-6 text-purple-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-gray-900 mb-3 group-hover:text-purple-700 transition-colors">
                      {service.title}
                    </h3>
                  </div>
                </div>

                <p className="text-gray-600 mb-6 leading-relaxed">
                  {service.description}
                </p>

                <ul className="space-y-2 mb-6">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-gray-600">
                      <div className="w-1.5 h-1.5 bg-purple-600 rounded-full mr-3"></div>
                      {feature}
                    </li>
                  ))}
                </ul>

                <Link
                  to={service.link}
                  className="inline-flex items-center text-purple-700 font-semibold hover:text-purple-800 transition-colors"
                >
                  Learn More
                  <SafeIcon icon={FiArrowRight} className="ml-2 w-4 h-4" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6 font-serif">
              Industry Expertise
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Deep sector knowledge across South Africa's key industries, ensuring relevant and actionable recommendations for every engagement.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {industries.map((industry, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className="relative h-48">
                  <img
                    src={industry.image}
                    alt={industry.name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">
                    {industry.name}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {industry.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6 font-serif">
              Our Proven Methodology
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A structured approach that combines international best practices with deep understanding of the South African business landscape.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              {
                step: '01',
                title: 'Discovery',
                description: 'Comprehensive analysis of current state and market dynamics'
              },
              {
                step: '02',
                title: 'Strategy',
                description: 'Development of tailored solutions aligned with local requirements'
              },
              {
                step: '03',
                title: 'Implementation',
                description: 'Structured execution with ongoing support and guidance'
              },
              {
                step: '04',
                title: 'Optimization',
                description: 'Continuous improvement and performance monitoring'
              }
            ].map((phase, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-purple-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                  {phase.step}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{phase.title}</h3>
                <p className="text-gray-600 leading-relaxed">{phase.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-purple-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-6 font-serif">
              Ready to Transform Your Business?
            </h2>
            <p className="text-xl text-gray-200 mb-8 max-w-2xl mx-auto">
              Let's discuss how our expertise can drive sustainable growth and operational excellence for your organisation.
            </p>
            <Link
              to="/contact"
              className="bg-yellow-400 text-purple-900 px-8 py-4 rounded-lg font-semibold hover:bg-yellow-300 transition-colors inline-flex items-center"
            >
              Schedule Your Consultation
              <SafeIcon icon={FiArrowRight} className="ml-2 w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Services;